import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/LabourAdvanceAPI/labourAdvanceAPI';

// Bulk Labour Advance (MOID 702). Every LabourAdvance endpoint returns HTTP 200 with
// IsSuccessful:false and the reason in Message when it refuses, so each thunk rejects on that.
const errMsg = (err, fallback) => {
    const detail = err?.ErrorDetails?.ErrorMessage;
    const msg = err?.Message || err?.message || (typeof err === 'string' ? err : fallback);
    return detail ? `${msg}: ${detail}` : msg;
};

export const uploadAdvanceStaging = createAsyncThunk(
    'labourAdvance/uploadStaging',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.uploadAdvanceStaging(payload);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Upload failed');
            return res?.Data || null;   // UploadBatchId (Guid)
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Upload failed'));
        }
    }
);

export const fetchAdvanceStagingPreview = createAsyncThunk(
    'labourAdvance/fetchStagingPreview',
    async (params, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceStagingPreview(params);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load preview');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load preview'));
        }
    }
);

export const generateLabourAdvance = createAsyncThunk(
    'labourAdvance/generate',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.generateAdvance(payload);
            // Budget / amount refusals: HTTP 200, IsSuccessful:false, reason in res.Message
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Advance generation failed');
            return res?.Data || res;
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Advance generation failed'));
        }
    }
);

export const fetchAdvanceDetail = createAsyncThunk(
    'labourAdvance/fetchDetail',
    async (advanceId, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceDetail(advanceId);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load advance');
            return res?.Data || null;
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load advance'));
        }
    }
);

export const fetchAdvanceInbox = createAsyncThunk(
    'labourAdvance/fetchInbox',
    async (roleId, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceInbox(roleId);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load advance inbox');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load advance inbox'));
        }
    }
);

export const submitApproveLabourAdvance = createAsyncThunk(
    'labourAdvance/approve',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.approveAdvance(payload);
            // 'not pending with your role', 'already Approved', next-level user missing: 200 + IsSuccessful:false
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Approval failed');
            return res?.Data || res;
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Approval failed'));
        }
    }
);

// ── Advance CMS ──
export const fetchAdvanceCMSBatches = createAsyncThunk(
    'labourAdvance/fetchCMSBatches',
    async (params, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceCMSBatches(params);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load approved advances');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load approved advances'));
        }
    }
);

export const fetchAdvanceCMSContractors = createAsyncThunk(
    'labourAdvance/fetchCMSContractors',
    async (advanceId, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceCMSContractors(advanceId);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load contractors');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load contractors'));
        }
    }
);

export const fetchAdvanceCMSWorkerGrid = createAsyncThunk(
    'labourAdvance/fetchCMSWorkerGrid',
    async (params, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceCMSWorkerGrid(params);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load workers');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load workers'));
        }
    }
);

export const saveLabourAdvanceCMS = createAsyncThunk(
    'labourAdvance/saveCMS',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveAdvanceCMS(payload);
            // PO / balance / labour type refusals: 200 + IsSuccessful:false, reason in Message
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Save failed');
            return res?.Data || res;
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Save failed'));
        }
    }
);

// ── Labour Advance Report (MOID 704) ──
export const fetchAdvanceReportCCs = createAsyncThunk(
    'labourAdvance/fetchReportCCs',
    async (params, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceReportCCs(params);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load cost centres');
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load cost centres'));
        }
    }
);

export const fetchAdvanceReport = createAsyncThunk(
    'labourAdvance/fetchReport',
    async (params, { rejectWithValue }) => {
        try {
            const res = await api.getAdvanceReport(params);
            if (res?.IsSuccessful === false) return rejectWithValue(res.Message || 'Failed to load report');
            return { Rows: res?.Data?.Rows || [], Lines: res?.Data?.Lines || [] };
        } catch (err) {
            return rejectWithValue(errMsg(err, 'Failed to load report'));
        }
    }
);

const initialState = {
    stagingLoading:  false,
    stagingError:    null,
    uploadBatchId:   null,

    previewRows:     [],
    previewLoading:  false,
    previewError:    null,

    generateLoading: false,
    generateResult:  null,
    generateError:   null,

    detail:          null,
    detailLoading:   false,
    detailError:     null,

    inbox:           [],
    inboxLoading:    false,
    inboxError:      null,

    approveLoading:  false,
    approveError:    null,

    cmsBatches:           [],
    cmsBatchesLoading:    false,
    cmsContractors:       [],
    cmsContractorsLoading: false,
    cmsWorkers:           [],
    cmsWorkersLoading:    false,
    cmsSaveLoading:       false,

    reportCCs:        [],
    reportCCsLoading: false,
    report:           null,     // { Rows, Lines }
    reportLoading:    false,
    reportError:      null,
};

const labourAdvanceSlice = createSlice({
    name: 'labourAdvance',
    initialState,
    reducers: {
        clearAdvanceState:   () => initialState,
        clearGenerateResult: (s) => { s.generateResult = null; s.generateError = null; },
        clearAdvanceDetail:  (s) => { s.detail = null; s.detailError = null; },
        clearAdvanceCMS:     (s) => {
            s.cmsBatches = []; s.cmsContractors = []; s.cmsWorkers = [];
            s.cmsBatchesLoading = false; s.cmsContractorsLoading = false; s.cmsWorkersLoading = false; s.cmsSaveLoading = false;
        },
        clearAdvanceReport:  (s) => { s.report = null; s.reportError = null; s.reportLoading = false; },
    },
    extraReducers: (b) => {
        b
        .addCase(uploadAdvanceStaging.pending,   (s) => { s.stagingLoading = true;  s.stagingError = null; s.uploadBatchId = null; })
        .addCase(uploadAdvanceStaging.fulfilled, (s, a) => { s.stagingLoading = false; s.uploadBatchId = a.payload; })
        .addCase(uploadAdvanceStaging.rejected,  (s, a) => { s.stagingLoading = false; s.stagingError = a.payload; })

        .addCase(fetchAdvanceStagingPreview.pending,   (s) => { s.previewLoading = true;  s.previewError = null; })
        .addCase(fetchAdvanceStagingPreview.fulfilled, (s, a) => { s.previewLoading = false; s.previewRows = a.payload; })
        .addCase(fetchAdvanceStagingPreview.rejected,  (s, a) => { s.previewLoading = false; s.previewError = a.payload; })

        .addCase(generateLabourAdvance.pending,   (s) => { s.generateLoading = true;  s.generateError = null; s.generateResult = null; })
        .addCase(generateLabourAdvance.fulfilled, (s, a) => { s.generateLoading = false; s.generateResult = a.payload; })
        .addCase(generateLabourAdvance.rejected,  (s, a) => { s.generateLoading = false; s.generateError = a.payload; })

        .addCase(fetchAdvanceDetail.pending,   (s) => { s.detailLoading = true;  s.detailError = null; })
        .addCase(fetchAdvanceDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload; })
        .addCase(fetchAdvanceDetail.rejected,  (s, a) => { s.detailLoading = false; s.detailError = a.payload; })

        .addCase(fetchAdvanceInbox.pending,   (s) => { s.inboxLoading = true;  s.inboxError = null; })
        .addCase(fetchAdvanceInbox.fulfilled, (s, a) => { s.inboxLoading = false; s.inbox = a.payload; })
        .addCase(fetchAdvanceInbox.rejected,  (s, a) => { s.inboxLoading = false; s.inboxError = a.payload; })

        .addCase(submitApproveLabourAdvance.pending,   (s) => { s.approveLoading = true;  s.approveError = null; })
        .addCase(submitApproveLabourAdvance.fulfilled, (s) => { s.approveLoading = false; })
        .addCase(submitApproveLabourAdvance.rejected,  (s, a) => { s.approveLoading = false; s.approveError = a.payload; })

        .addCase(fetchAdvanceCMSBatches.pending,   (s) => { s.cmsBatchesLoading = true; s.cmsBatches = []; })
        .addCase(fetchAdvanceCMSBatches.fulfilled, (s, a) => { s.cmsBatchesLoading = false; s.cmsBatches = a.payload; })
        .addCase(fetchAdvanceCMSBatches.rejected,  (s) => { s.cmsBatchesLoading = false; })

        .addCase(fetchAdvanceCMSContractors.pending,   (s) => { s.cmsContractorsLoading = true; s.cmsContractors = []; })
        .addCase(fetchAdvanceCMSContractors.fulfilled, (s, a) => { s.cmsContractorsLoading = false; s.cmsContractors = a.payload; })
        .addCase(fetchAdvanceCMSContractors.rejected,  (s) => { s.cmsContractorsLoading = false; })

        .addCase(fetchAdvanceCMSWorkerGrid.pending,   (s) => { s.cmsWorkersLoading = true; s.cmsWorkers = []; })
        .addCase(fetchAdvanceCMSWorkerGrid.fulfilled, (s, a) => { s.cmsWorkersLoading = false; s.cmsWorkers = a.payload; })
        .addCase(fetchAdvanceCMSWorkerGrid.rejected,  (s) => { s.cmsWorkersLoading = false; })

        .addCase(saveLabourAdvanceCMS.pending,   (s) => { s.cmsSaveLoading = true; })
        .addCase(saveLabourAdvanceCMS.fulfilled, (s) => { s.cmsSaveLoading = false; })
        .addCase(saveLabourAdvanceCMS.rejected,  (s) => { s.cmsSaveLoading = false; })

        .addCase(fetchAdvanceReportCCs.pending,   (s) => { s.reportCCsLoading = true; })
        .addCase(fetchAdvanceReportCCs.fulfilled, (s, a) => { s.reportCCsLoading = false; s.reportCCs = a.payload; })
        .addCase(fetchAdvanceReportCCs.rejected,  (s) => { s.reportCCsLoading = false; s.reportCCs = []; })

        .addCase(fetchAdvanceReport.pending,   (s) => { s.reportLoading = true; s.reportError = null; })
        .addCase(fetchAdvanceReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
        .addCase(fetchAdvanceReport.rejected,  (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = null; });
    },
});

export const { clearAdvanceState, clearGenerateResult, clearAdvanceDetail, clearAdvanceCMS, clearAdvanceReport } = labourAdvanceSlice.actions;
export default labourAdvanceSlice.reducer;
